/**
 * BgmPlayer — chooses between programmatic synth (MusicPlayer) and downloaded
 * MP3 files at /assets/bgm/*.mp3 (Music Director asset set).
 *
 * Synth starts immediately so audio never blocks gameplay. Each file loads
 * asynchronously; once a track hits canplaythrough, that specific track flips
 * to file mode independently — no waiting for all 13 tracks to be ready.
 *
 * If any file fails to load (HTTP 404, decoder error), that track stays in
 * synth mode forever for this session — no console error, no visible degradation.
 *
 * Tracks without a dedicated synth loop fall back to the closest existing loop:
 *   peaceful (title / tutorial / ending) → ACADEMY_LOOP_NOTES
 *   stage themes                         → BATTLE_LOOP_NOTES
 *   boss-tier2 / boss-tier3             → BOSS_LOOP_NOTES
 *
 * Public API mirrors MusicPlayer so call sites can swap with no other changes.
 */

import { MusicPlayer } from './music';
import { BATTLE_LOOP_NOTES, LOOP_DURATION_SEC, Note } from './battle-loop';
import { ACADEMY_LOOP_NOTES, ACADEMY_LOOP_DURATION_SEC } from './academy-loop';
import { BOSS_LOOP_NOTES, BOSS_LOOP_DURATION_SEC } from './boss-loop';
import type { FactionId } from '../../shared/types';
import { getFactionBgmPreset } from './faction-bgm-presets';

export type TrackName =
  | 'title'
  | 'tutorial'
  | 'battle'
  | 'boss'
  | 'academy'
  | 'stage-xingyuan'
  | 'stage-fuwen'
  | 'stage-jixie'
  | 'stage-ziran'
  | 'stage-jiangyu'
  | 'boss-tier2'
  | 'boss-tier3'
  | 'ending';

const FILE_PATHS: Record<TrackName, string> = {
  title:            '/assets/bgm/title.mp3',
  tutorial:         '/assets/bgm/tutorial.mp3',
  battle:           '/assets/bgm/battle.mp3',
  boss:             '/assets/bgm/boss.mp3',
  academy:          '/assets/bgm/academy.mp3',
  'stage-xingyuan': '/assets/bgm/stage-xingyuan.mp3',
  'stage-fuwen':    '/assets/bgm/stage-fuwen.mp3',
  'stage-jixie':    '/assets/bgm/stage-jixie.mp3',
  'stage-ziran':    '/assets/bgm/stage-ziran.mp3',
  'stage-jiangyu':  '/assets/bgm/stage-jiangyu.mp3',
  'boss-tier2':     '/assets/bgm/boss-tier2.mp3',
  'boss-tier3':     '/assets/bgm/boss-tier3.mp3',
  ending:           '/assets/bgm/ending.mp3',
};

const NOTE_LOOPS: Record<TrackName, { notes: Note[]; durationSec: number }> = {
  // Peaceful / ambient fallback → academy loop
  title:            { notes: ACADEMY_LOOP_NOTES, durationSec: ACADEMY_LOOP_DURATION_SEC },
  tutorial:         { notes: ACADEMY_LOOP_NOTES, durationSec: ACADEMY_LOOP_DURATION_SEC },
  academy:          { notes: ACADEMY_LOOP_NOTES, durationSec: ACADEMY_LOOP_DURATION_SEC },
  ending:           { notes: ACADEMY_LOOP_NOTES, durationSec: ACADEMY_LOOP_DURATION_SEC },
  // Stage themes → battle loop
  battle:           { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  'stage-xingyuan': { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  'stage-fuwen':    { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  'stage-jixie':    { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  'stage-ziran':    { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  'stage-jiangyu':  { notes: BATTLE_LOOP_NOTES, durationSec: LOOP_DURATION_SEC },
  // Boss variants → boss loop
  boss:             { notes: BOSS_LOOP_NOTES, durationSec: BOSS_LOOP_DURATION_SEC },
  'boss-tier2':     { notes: BOSS_LOOP_NOTES, durationSec: BOSS_LOOP_DURATION_SEC },
  'boss-tier3':     { notes: BOSS_LOOP_NOTES, durationSec: BOSS_LOOP_DURATION_SEC },
};

export class BgmPlayer {
  private synth: MusicPlayer;
  private files = new Map<TrackName, HTMLAudioElement>();
  private mode: 'synth' | 'file' = 'synth';
  private currentTrack: TrackName = 'battle';
  private started = false;
  private fileVolume = 0.25;
  // Faction-BGM-variant state (IDEA-014). null = neutral / no variant.
  private factionVariant: FactionId | null = null;

  /**
   * Web Audio chain for file-mode (mp3) tracks (task #9).
   * Each <audio> element goes through createMediaElementSource ONCE (browser
   * spec) → cached in `sourceCache` → BiquadFilter → GainNode → destination.
   *
   * Null when AudioContext isn't available (legacy / test stub) or when any
   * node setup throws — caller transparently falls back to plain
   * <audio>.playbackRate only (no filter), same as pre-task-9 behaviour.
   */
  private audioCtx: AudioContext | null = null;
  private fileFilter: BiquadFilterNode | null = null;
  private fileGain: GainNode | null = null;
  private sourceCache = new Map<TrackName, MediaElementAudioSourceNode>();

  /**
   * W1 音效變化包: boss 進場/離場 BGM 淡入淡出(≥300ms,不硬切)。
   * fadeTimer drives a setInterval volume ramp; fadeFinalize snaps the
   * in-flight fade to its end state when a new fade/stop preempts it.
   */
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private fadeFinalize: (() => void) | null = null;
  /** Crossfade duration (ms). ≥300ms per GAME_OVERHAUL_PLAN §2 W1. */
  static readonly TRACK_FADE_MS = 700;
  private static readonly FADE_STEP_MS = 30;

  constructor() {
    this.synth = new MusicPlayer();
    this.attemptFileLoad();
  }

  /**
   * Lazily create the shared file-mode Web Audio chain. Idempotent.
   * Returns false (and leaves all fields null) when the host has no
   * AudioContext, when createBiquadFilter/createGain/createMediaElementSource
   * are missing, or when any node setup throws — gracefully falls back to
   * plain <audio>.playbackRate routing.
   */
  private ensureFileAudioChain(): boolean {
    if (this.audioCtx && this.fileFilter && this.fileGain) return true;
    try {
      const Ctor: (new () => AudioContext) | undefined =
        (globalThis as unknown as { AudioContext?: new () => AudioContext }).AudioContext
        ?? (globalThis as unknown as { webkitAudioContext?: new () => AudioContext }).webkitAudioContext;
      if (!Ctor) return false;
      const ctx = new Ctor();
      const hasFilter = typeof (ctx as unknown as { createBiquadFilter?: () => BiquadFilterNode }).createBiquadFilter === 'function';
      const hasGain = typeof (ctx as unknown as { createGain?: () => GainNode }).createGain === 'function';
      const hasSource = typeof (ctx as unknown as { createMediaElementSource?: (el: HTMLAudioElement) => MediaElementAudioSourceNode }).createMediaElementSource === 'function';
      if (!hasFilter || !hasGain || !hasSource) return false;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 20_000;
      filter.Q.value = 0.707;
      // fileGain stays at 1.0 (passthrough) — volume is controlled by each
       // <audio> element's .volume property, which still applies even when
       // the element is routed through createMediaElementSource. Keeping the
       // gain node in the chain leaves room for a future ducking / fade API
       // without re-wiring everything.
      const gain = ctx.createGain();
      gain.gain.value = 1.0;
      filter.connect(gain);
      gain.connect(ctx.destination);
      this.audioCtx = ctx;
      this.fileFilter = filter;
      this.fileGain = gain;
      // Re-apply current faction preset to the brand-new filter so a player
      // whose variant was set BEFORE the chain existed still hears it.
      const preset = getFactionBgmPreset(this.factionVariant);
      this.applyFileFilter(preset.filterType, preset.filterCutoffHz, preset.filterQ);
      return true;
    } catch {
      this.audioCtx = null;
      this.fileFilter = null;
      this.fileGain = null;
      return false;
    }
  }

  /**
   * Route an <audio> element through the shared filter chain (idempotent).
   * createMediaElementSource may only be called ONCE per HTMLMediaElement
   * (browser spec) — cached in sourceCache to enforce that.
   * Returns false if the chain isn't healthy or the call throws.
   */
  private routeAudioThroughFilter(track: TrackName, audio: HTMLAudioElement): boolean {
    if (!this.ensureFileAudioChain()) return false;
    if (this.sourceCache.has(track)) return true;
    try {
      const src = this.audioCtx!.createMediaElementSource(audio);
      src.connect(this.fileFilter!);
      this.sourceCache.set(track, src);
      return true;
    } catch {
      return false;
    }
  }

  /** Apply filter settings to the shared file-mode filter (no-op if chain absent). */
  private applyFileFilter(type: 'lowpass' | 'highpass' | 'none', cutoffHz: number, Q: number): void {
    if (!this.fileFilter) return;
    if (type === 'none') {
      this.fileFilter.type = 'lowpass';
      this.fileFilter.frequency.value = 20_000;
      this.fileFilter.Q.value = 0.707;
    } else {
      this.fileFilter.type = type;
      this.fileFilter.frequency.value = Math.max(20, Math.min(20_000, cutoffHz));
      this.fileFilter.Q.value = Math.max(0.1, Math.min(20, Q));
    }
  }

  private attemptFileLoad(): void {
    for (const name of Object.keys(FILE_PATHS) as TrackName[]) {
      const audio = new Audio(FILE_PATHS[name]);
      audio.loop = true;
      audio.volume = this.fileVolume;
      audio.preload = 'auto';
      audio.addEventListener('canplaythrough', () => {
        this.files.set(name, audio);
        // If this is the currently-playing track and we're in synth mode, upgrade now
        if (this.currentTrack === name && this.started && this.mode === 'synth') {
          this.synth.stop();
          this.mode = 'file';
          this.playFile(name);
        }
      }, { once: true });
      audio.addEventListener('error', () => {
        // Stay on synth for this track; no degradation visible to user
      }, { once: true });
      audio.load();
    }
  }

  private playFile(track: TrackName): void {
    for (const [name, audio] of this.files) {
      if (name !== track) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
    const target = this.files.get(track);
    if (target) {
      target.currentTime = 0;
      // Defensive: a cancelled fade may have left this element below the
      // user-set volume — hard (non-faded) switches always restore it.
      target.volume = this.fileVolume;
      target.playbackRate = getFactionBgmPreset(this.factionVariant).playbackRate;
      // Route through Web Audio filter chain (task #9) so faction filter
      // preset reaches the mp3 path too. fileGain stays passthrough — volume
      // is still controlled by target.volume (set elsewhere via setVolume).
      // When routing fails we transparently fall back to plain <audio>.
      this.routeAudioThroughFilter(track, target);
      // play() returns a Promise that rejects if no user gesture yet — swallow
      void target.play().catch(() => { /* awaiting user gesture; will retry on next switchTrack */ });
    }
  }

  /**
   * IDEA-014: faction BGM variant. Sets a pitch/tempo tweak based on the
   * player's strongest faction; null = neutral. Applies to both synth
   * (next loop) and any currently playing file track.
   *
   * Safe to call at any time. Volume is NOT changed — only pitch.
   */
  setFactionVariant(factionId: FactionId | null): void {
    this.factionVariant = factionId;
    const preset = getFactionBgmPreset(factionId);
    // Synth path: next-loop notes pick up new pitch + filter tilt immediately.
    this.synth.setPitchShift(preset.playbackRate);
    this.synth.setFilter(preset.filterType, preset.filterCutoffHz, preset.filterQ);
    // File path: <audio>.playbackRate handles pitch+tempo per-element. The
    // filter ride along the shared Web Audio chain (task #9) — touched once
    // here, applies to every routed element automatically.
    for (const audio of this.files.values()) {
      audio.playbackRate = preset.playbackRate;
    }
    this.applyFileFilter(preset.filterType, preset.filterCutoffHz, preset.filterQ);
  }

  /** Current faction variant (null = neutral). Exposed for tests / debug. */
  getFactionVariant(): FactionId | null {
    return this.factionVariant;
  }

  /** Mirrors MusicPlayer.start — accepts optional notes/duration for synth back-compat. */
  start(notes: Note[] = BATTLE_LOOP_NOTES, loopDurationSec: number = LOOP_DURATION_SEC): void {
    this.started = true;
    // Identify which track based on notes reference
    if (notes === BOSS_LOOP_NOTES) this.currentTrack = 'boss';
    else if (notes === ACADEMY_LOOP_NOTES) this.currentTrack = 'academy';
    else this.currentTrack = 'battle';

    if (this.mode === 'file' && this.files.has(this.currentTrack)) {
      this.playFile(this.currentTrack);
    } else {
      this.synth.start(notes, loopDurationSec);
    }
  }

  stop(): void {
    this.started = false;
    // Abort any in-flight track fade WITHOUT finalizing (finalize could
    // restart playback) — everything is being silenced right below anyway.
    this.cancelFade(false);
    this.synth.stop();
    for (const audio of this.files.values()) {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = this.fileVolume; // undo any half-finished fade ramp
    }
  }

  /** Set master BGM volume (0..1). Applies immediately to all loaded file tracks
   *  and propagates to the synth's master gain. */
  setVolume(v: number): void {
    this.fileVolume = Math.max(0, Math.min(1, v));
    for (const audio of this.files.values()) {
      audio.volume = this.fileVolume;
    }
    // Scale synth master gain proportionally (synth uses 0.5 as its base)
    this.synth.setMasterGain(this.fileVolume * 0.5);
  }

  // ── Existing convenience methods ──────────────────────────────────────────
  playAcademyBgm(): void { this.switchToTrack('academy'); }
  playBattleBgm():  void { this.switchToTrack('battle'); }
  playBossBgm():    void { this.switchToTrack('boss'); }

  /**
   * Task #11: switch to the boss BGM during battle, preserving the player's
   * current faction-variant preset (pitch + filter). Used by
   * horizontal-battle-scene on boss spawn.
   *
   * The underlying switch already inherits the faction preset via
   * playFile() (which reads factionVariant) and the shared file filter chain
   * (set by setFactionVariant). We re-apply the preset explicitly anyway as
   * a belt-and-braces guard against future switchToTrack refactors quietly
   * resetting the variant.
   *
   * W1 音效變化包: switches now CROSSFADE (≥300ms) instead of hard-cutting —
   * boss 進場/離場的 BGM 轉場不再「啪」一聲。
   */
  switchToBossTheme(): void {
    this.switchToTrackFaded('boss');
    this.reapplyFactionPreset();
  }

  /**
   * Task #11: switch back to the battle BGM (e.g. after boss defeat). Same
   * preset-preservation + fade guarantee as switchToBossTheme().
   */
  switchToBattleTheme(): void {
    this.switchToTrackFaded('battle');
    this.reapplyFactionPreset();
  }

  /**
   * Clear any in-flight fade interval. When `finalize` is true the fade
   * jumps straight to its end state (old track paused + volume restored,
   * new track at full user volume) so back-to-back switches stay clean.
   */
  private cancelFade(finalize: boolean): void {
    if (this.fadeTimer !== null) {
      clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
    const f = this.fadeFinalize;
    this.fadeFinalize = null;
    if (finalize && f) f();
  }

  /**
   * W1 音效變化包: switch tracks with a volume crossfade instead of a hard
   * cut. Two paths:
   *
   *  - file mode (target mp3 decoded): start the target at volume 0 and ramp
   *    it up to the user volume while the outgoing <audio> ramps down over
   *    `fadeMs` (default 700ms — both directions well above the 300ms spec
   *    floor). The outgoing element is paused + volume-restored at the end.
   *  - synth fallback (target mp3 missing / still loading): ramp the synth
   *    master gain to 0 over the first half, switch tracks at the midpoint,
   *    ramp back up over the second half.
   *
   * `currentTrack` is updated SYNCHRONOUSLY so callers/tests observe the
   * switch immediately; only the audible volumes ramp over time. Volume
   * always lands exactly on the user-set fileVolume (parentSettings 靜音 /
   * 音量設定不被覆寫 — fade 每一步都從 this.fileVolume 重算)。
   */
  switchToTrackFaded(track: TrackName, fadeMs: number = BgmPlayer.TRACK_FADE_MS): void {
    if (track === this.currentTrack) return; // already there — nothing to fade
    // Preempt any in-flight fade by snapping it to its end state first.
    this.cancelFade(true);

    const fromTrack = this.currentTrack;
    const target = this.files.get(track);
    const stepMs = BgmPlayer.FADE_STEP_MS;

    if (target) {
      // ── file crossfade path ────────────────────────────────────────────
      const from = this.mode === 'file' ? this.files.get(fromTrack) : undefined;
      const synthWasActive = this.mode === 'synth';
      this.currentTrack = track;
      this.mode = 'file';
      // Stop any stray third tracks outright (only from/target take part).
      for (const audio of this.files.values()) {
        if (audio !== target && audio !== from) {
          audio.pause();
          audio.currentTime = 0;
        }
      }
      target.currentTime = 0;
      target.playbackRate = getFactionBgmPreset(this.factionVariant).playbackRate;
      this.routeAudioThroughFilter(track, target);
      target.volume = 0; // fade-in starts from silence
      void target.play().catch(() => { /* awaiting user gesture */ });

      const steps = Math.max(1, Math.ceil(fadeMs / stepMs));
      let step = 0;
      const finalize = () => {
        target.volume = this.fileVolume;
        if (from && from !== target) {
          from.pause();
          from.currentTime = 0;
          from.volume = this.fileVolume; // restore for its next (re)use
        }
        if (synthWasActive) {
          this.synth.stop();
          this.synth.setMasterGain(this.fileVolume * 0.5);
        }
      };
      this.fadeFinalize = finalize;
      this.fadeTimer = setInterval(() => {
        step++;
        const t = Math.min(1, step / steps);
        target.volume = this.fileVolume * t;
        if (from && from !== target) from.volume = this.fileVolume * (1 - t);
        if (synthWasActive) this.synth.setMasterGain(this.fileVolume * 0.5 * (1 - t));
        if (t >= 1) this.cancelFade(true); // runs finalize exactly once
      }, stepMs);
    } else {
      // ── synth fallback: gain dip → switch at midpoint → gain recover ───
      this.currentTrack = track;
      const base = () => this.fileVolume * 0.5;
      const steps = Math.max(2, Math.ceil(fadeMs / stepMs));
      const half = Math.ceil(steps / 2);
      let step = 0;
      let switched = false;
      const doSwitch = () => {
        if (switched) return;
        switched = true;
        this.switchToTrack(track);
      };
      const finalize = () => {
        doSwitch();
        this.synth.setMasterGain(base());
      };
      this.fadeFinalize = finalize;
      this.fadeTimer = setInterval(() => {
        step++;
        if (step <= half) {
          this.synth.setMasterGain(base() * (1 - step / half));
          if (step === half) doSwitch();
        } else {
          const t = (step - half) / (steps - half);
          this.synth.setMasterGain(base() * Math.min(1, t));
          if (step >= steps) this.cancelFade(true);
        }
      }, stepMs);
    }
  }

  /**
   * Re-apply the current faction variant's pitch + filter to BOTH synth and
   * file paths. Idempotent — safe to call after any track switch.
   */
  private reapplyFactionPreset(): void {
    const preset = getFactionBgmPreset(this.factionVariant);
    this.synth.setPitchShift(preset.playbackRate);
    this.synth.setFilter(preset.filterType, preset.filterCutoffHz, preset.filterQ);
    for (const audio of this.files.values()) {
      audio.playbackRate = preset.playbackRate;
    }
    this.applyFileFilter(preset.filterType, preset.filterCutoffHz, preset.filterQ);
  }

  // ── New convenience methods ───────────────────────────────────────────────
  playTitleBgm():   void { this.switchToTrack('title'); }
  playTutorialBgm():void { this.switchToTrack('tutorial'); }

  playStageBgm(faction: 'celestial' | 'runic' | 'techno' | 'elemental' | 'dominion'): void {
    const map = {
      celestial: 'stage-xingyuan',
      runic:     'stage-fuwen',
      techno:    'stage-jixie',
      elemental: 'stage-ziran',
      dominion:  'stage-jiangyu',
    } as const;
    this.switchToTrack(map[faction]);
  }

  playBossTier2Bgm(): void { this.switchToTrack('boss-tier2'); }
  playBossTier3Bgm(): void { this.switchToTrack('boss-tier3'); }
  playEndingBgm():    void { this.switchToTrack('ending'); }

  private switchToTrack(track: TrackName): void {
    this.currentTrack = track;
    if (this.mode === 'file' && this.files.has(track)) {
      this.playFile(track);
    } else {
      const loop = NOTE_LOOPS[track];
      this.synth.switchTrack(loop.notes, loop.durationSec);
      // If file for this track already loaded but we were in synth for another track,
      // upgrade mode for this track immediately
      if (this.files.has(track)) {
        this.synth.stop();
        this.mode = 'file';
        this.playFile(track);
      }
    }
  }
}

/**
 * Play a short pitched "stinger" cue tinted by the faction BGM preset.
 * Oneshot envelope; reuses faction-bgm-presets.ts playbackRate for pitch tint.
 * Used by the skill level-up Pokémon-evolution overlay. No-op if WebAudio
 * unavailable (test env, locked-down browser) — fails silent.
 */
export function playFactionStinger(faction: FactionId, durationMs: number): void {
  const clamped = Math.max(50, Math.min(10_000, durationMs));
  const preset = getFactionBgmPreset(faction);

  try {
    const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext
      ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = 440 * preset.playbackRate;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + clamped / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + clamped / 1000 + 0.05);
  } catch {
    // Silent — audio failures must not break UI flow
  }
}
